import { useState } from 'react';
import { ChevronRight, Plus } from 'lucide-react';
import type { WhatsappGroup } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { CONNECTORS_PAGE } from '../../../../lib/use-connectors';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { useLoadBi } from '../../../../lib/use-load-bi';
import { useTranslation } from '../../../../lib/i18n';
import { useProductName } from '../../../../lib/use-brand';
import { useNeedsPhoneConnect, useOpenPhoneConnect } from '../../../../lib/phone-connect';
import { useWhatsappGroups } from '../../../settings/shared/settings-queries';
import { GroupCard } from '../../settings-whatsapp/GroupCard';
import { GroupFormPanel } from '../../settings-whatsapp/GroupFormPanel';
import { GroupSettingsModal } from '../../settings-whatsapp/GroupSettingsModal';
import { useOpenWhatsappGroup } from '../../settings-whatsapp/use-open-group';
import { WhatsAppGlyph } from '../../settings-whatsapp/WhatsAppGlyph';
import { WHATSAPP_DIRECT_URL, MAX_LIVE_GROUPS } from '../../settings-whatsapp/constants';
import { Button, Card, Typography } from '../../ui';
import styles from './mobile.module.css';

/**
 * The WhatsApp channel, mobile (Figma 13131-79203): a two-line "WhatsApp Groups / N groups" summary
 * card that expands to the assistant-joined groups, then the two primary CTAs as full-width buttons —
 * "Create a new group" and "Chat with {productName} on WhatsApp". Reuses the shared group hooks + the
 * create/settings modals verbatim (the live-group cap, invite links, rename, delete are unchanged);
 * only the framing + copy are mobile (mobile-only i18n keys, desktop untouched).
 */
export function WhatsappMobile() {
  const { t } = useTranslation();
  const productName = useProductName();
  const { logPageClickBi } = useGenericBi();
  const { groups, loading, error } = useWhatsappGroups();
  useLoadBi('whatsapp-groups-data', { isLoading: loading, isError: error });
  const [groupsOpen, setGroupsOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const { group: settingsGroup, openGroup, closeGroup } = useOpenWhatsappGroup(groups);

  const atCap = groups.length >= MAX_LIVE_GROUPS;
  // The same gate the desktop hub applies — see `WhatsappChannelSection`.
  const needsPhone = useNeedsPhoneConnect();
  const openPhoneConnect = useOpenPhoneConnect();
  // Qualified by the outcome when gated — see the note on the desktop hub.
  const clicked = (button_name: string) =>
    logPageClickBi({ ...CONNECTORS_PAGE, button_name }, needsPhone ? { componentName: 'connect-phone' } : undefined);

  const open = (group: WhatsappGroup) => {
    logPageClickBi({ ...CONNECTORS_PAGE, button_name: 'open_group' }, { componentName: 'open-group' });
    openGroup(group);
  };

  const newGroup = () => {
    clicked('new_group');
    if (needsPhone) {
      openPhoneConnect();
      return;
    }
    setCreating(true);
  };

  const openChat = () => clicked('open_whatsapp');

  return (
    <div className={styles.channelBody}>
      {groups.length > 0 && (
        <>
          <Card
            component="button"
            interactive
            size="sm"
            className={styles.mcpRow}
            aria-expanded={groupsOpen}
            onClick={() => {
              logPageClickBi({ ...CONNECTORS_PAGE, button_name: 'open_groups' });
              setGroupsOpen((v) => !v);
            }}
          >
            <span className={styles.connectTile} aria-hidden>
              <WhatsAppGlyph size={18} />
            </span>
            <div className={styles.rowText}>
              <div className={styles.rowName}>{t('connectors.whatsapp.mobile.title')}</div>
              <div className={styles.rowSub}>
                {t(groups.length === 1 ? 'connectors.whatsapp.mobile.countOne' : 'connectors.whatsapp.mobile.count', {
                  count: groups.length,
                })}
              </div>
            </div>
            <ChevronRight size={16} aria-hidden className={`${styles.rowChevron} ${groupsOpen ? styles.chevronTurn : ''}`} />
          </Card>

          {groupsOpen && (
            <div className={styles.list}>
              {groups.map((group) => (
                <GroupCard key={group.id} group={group} selected={false} onOpen={() => open(group)} />
              ))}
            </div>
          )}
        </>
      )}

      {atCap && (
        <Typography variant="p3" color="muted">
          {t('settings.whatsapp.groups.capReached')}
        </Typography>
      )}

      <Button fullWidth size="large" startIcon={<Plus size={16} aria-hidden />} disabled={atCap} onClick={newGroup}>
        {t('connectors.whatsapp.mobile.newGroup')}
      </Button>
      {/* Gated, it must not be a link at all — see the note on the desktop hub's Chat CTA. */}
      {needsPhone ? (
        <Button
          fullWidth
          size="large"
          variant="outlined"
          startIcon={<WhatsAppGlyph />}
          onClick={() => {
            openChat();
            openPhoneConnect();
          }}
        >
          {t('connectors.whatsapp.mobile.chat', { productName })}
        </Button>
      ) : (
        <Button
          fullWidth
          size="large"
          variant="outlined"
          href={WHATSAPP_DIRECT_URL}
          external
          target="_blank"
          rel="noreferrer"
          startIcon={<WhatsAppGlyph />}
          onClick={openChat}
        >
          {t('connectors.whatsapp.mobile.chat', { productName })}
        </Button>
      )}

      {creating && <GroupFormPanel onDone={() => setCreating(false)} />}
      {settingsGroup && <GroupSettingsModal group={settingsGroup} onClose={closeGroup} />}
    </div>
  );
}
